import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, retry, RetryConfig, throwError, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthenticationService } from '@app/auth';
import { ErrorMessage, HttpQueryInterface, HttpRequestType } from '@shared/http/http.service';
import { ErrorInputs, ErrorService } from '@shared/services/error.service';
import { Location } from '@angular/common';
import { I18nService } from '@app/i18n';
import { getViaSurveyEndpointUrl, ViaSurveyEndpoint } from '@shared/models/viaSurvey.model';
import { UrlEndpoint } from '@app/utils/url';
import { ApiUrlStep } from '@app/courseContent/courseContent.service';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorHandlerInterceptor implements HttpInterceptor {
  private readonly MAX_NUMBER_OF_RETRY_NO_CONNECTION: number = 5;
  private readonly RETRY_DELAY: number = 500;

  private readonly httpRequestToIgnoreInErrorHandler: HttpQueryInterface[] =
    this.getAllHttpRequestToIgnoreInErrorHandler();

  private retryConfig: RetryConfig = {
    count: this.MAX_NUMBER_OF_RETRY_NO_CONNECTION,
    delay: this.retryConfigDelayFn.bind(this),
  };

  constructor(
    private authenticationService: AuthenticationService,
    private errorService: ErrorService,
    private i18nService: I18nService,
    private location: Location,
  ) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retry(this.retryConfig),
      catchError((error) => this.errorHandler(request, error)),
    );
  }

  private retryConfigDelayFn(error: HttpErrorResponse, retryAttempt: number): Observable<number> {
    console.log('TONIO ErrorHandlerInterceptor delay error=', error, '   retryAttempt=', retryAttempt);
    if (retryAttempt < this.MAX_NUMBER_OF_RETRY_NO_CONNECTION && this.isErrorCodeNoConnectionWithServer(error)) {
      const retryDelay: number = retryAttempt * this.RETRY_DELAY;
      const errorMessageBugsnag: string = `Retry ${retryAttempt}: -1 or 0 error when communicating with Server, going to retry in ${retryDelay}`;
      console.error(`${errorMessageBugsnag} error=`, error);
      this.errorService.notifyBugsnag(errorMessageBugsnag);
      return timer(retryDelay);
    }

    return throwError(() => error);
  }

  private isErrorCodeNoConnectionWithServer(error: HttpErrorResponse): boolean {
    console.log('TONIO RetryInterceptor isErrorCodeNoConnectionWithServer error=', error);
    return error.status === -1 || error.status === 0;
  }

  private errorHandler(request: HttpRequest<any>, error: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (error.status === 401) {
      if (request.url.includes(`survey`) && error.error?.message === 'token_used') {
        this.errorService.showError({
          title: this.i18nService.translate(`friends_survey.already_submitted`),
          body: this.i18nService.translate(`friends_survey.contact_support`),
          hideButtons: true,
        });
      } else if (
        !request.url.includes(`survey`) &&
        !request.url.includes(`authenticate`) &&
        !request.url.includes(`password`) &&
        !this.location.path(true).includes('login')
      ) {
        this.authenticationService.logout({
          redirect: this.location.path(true),
          errorMessage: error.statusText as ErrorMessage,
        });
      }
    } else {
      const isErrorAlreadyHandledDirectlyByComponent: boolean = this.isErrorAlreadyHandledDirectlyByComponent(request);
      if (!isErrorAlreadyHandledDirectlyByComponent) {
        this.errorService.notifyBugsnag(error);
        this.errorService.showError(this.getErrorToDisplay(error));
      }

      console.log(
        'TONIO ErrorHandlerInterceptor errorHandler isErrorAlreadyHandledDirectlyByComponent=',
        isErrorAlreadyHandledDirectlyByComponent,
      );
    }

    console.log('TONIO ErrorHandlerInterceptor errorHandler request=', request, '   error=', error);
    throw error;
  }

  private getErrorToDisplay(error: HttpErrorResponse): ErrorInputs {
    if (this.isErrorCodeNoConnectionWithServer(error)) {
      return {
        title: this.i18nService.translate(`errors.connection_with_server.title`),
        body: this.i18nService.translate(`errors.connection_with_server.body`),
      };
    }

    // Will display default error message
    return {};
  }

  private isErrorAlreadyHandledDirectlyByComponent(request: HttpRequest<any>): boolean {
    console.log('TONIO ErrorHandlerInterceptor isErrorAlreadyHandledDirectlyByComponent request=', request);

    const requestFullUrl: string = request.url;
    const requestMethod: HttpRequestType = request.method as HttpRequestType;

    return !!this.httpRequestToIgnoreInErrorHandler.find((requestToIgnore: HttpQueryInterface): boolean => {
      // requestFullUrl will be prefixed by `api` and might include dynamic params, it's simpler and as reliable to just
      // check if part of the url is the same as we don't have that many endpoints
      const urlMatch: boolean = requestFullUrl.includes(requestToIgnore.url);
      return requestMethod === requestToIgnore.type && urlMatch;
    });
  }

  private getAllHttpRequestToIgnoreInErrorHandler(): HttpQueryInterface[] {
    return [
      {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.registerUser),
      },
      {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.loginUser),
      },
      {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.startSurvey),
      },
      {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.getQuestions),
      },
      {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.getResults),
      },
      {
        type: HttpRequestType.POST,
        url: getViaSurveyEndpointUrl(ViaSurveyEndpoint.submitAnswers),
      },
      {
        type: HttpRequestType.GET,
        url: ApiUrlStep.step,
      },
      {
        type: HttpRequestType.GET,
        url: UrlEndpoint.isMaintenanceDisabled,
      },
      {
        type: HttpRequestType.GET,
        url: UrlEndpoint.participants,
      },
      {
        type: HttpRequestType.PUT,
        url: UrlEndpoint.isMaintenanceDisabled,
      },
      {
        type: HttpRequestType.PUT,
        url: UrlEndpoint.lifemapLogsPrefix,
      },
      {
        type: HttpRequestType.POST,
        url: UrlEndpoint.authenticate,
      },
    ];
  }
}
